import { useQuery } from "@apollo/client";
import { useState, useEffect, useMemo, useContext } from "react";
import { useSelector } from "react-redux";
import { uniqBy, sortBy, isEmpty } from "lodash";
import {
  GET_ALL_USERS,
  GET_ALL_USERS_ACTIVITY_CENTER,
  GET_ALL_USERS_APPOINTMENT_FORM,
  GET_ALL_USERS_TEAMS,
  GET_FILTER_USERS,
} from "../graphql/query";
import { getCompanyId } from "../redux/slices/companySlice";
import { UserContext } from "../amplify/authenticator-provider";

export const USER_STATUS = {
  ACTIVATED: "ACTIVATED",
  DEACTIVATED: "DEACTIVATED",
};

// Export available queries for external use
export const USER_QUERIES = {
  FILTER: GET_FILTER_USERS, // Default query used in the hook
  ACTIVITY_CENTER: GET_ALL_USERS_ACTIVITY_CENTER,
  USER_APPOINTMENT: GET_ALL_USERS_APPOINTMENT_FORM,
  TEAM_USERS: GET_ALL_USERS_TEAMS,
  ALL: GET_ALL_USERS,
};

// Preload payload generators based on GET_FILTER_USERS query
// Available query variables:
// - page: Int, pageSize: Int, search: String, companyId: Int! (required)
// - isGlobalLeadOwner: Boolean, isLocationFilter: Boolean, locationIds: [Int]
// - allUser: Boolean, companyRoles: [Int], isActive: Boolean
// - status: [String], isAllLocationUsers: Boolean

// Helper to parse locationIds from sessionStorage (handles comma-separated values)
const getSessionLocationIds = () => {
  const currentLocationId = sessionStorage.getItem("currentLocationId");
  if (!currentLocationId) return { locationIds: [], isAllLocationSelected: false };

  const isAllLocationSelected = currentLocationId.includes(",");
  const locationIds = isAllLocationSelected
    ? currentLocationId.split(",").map(loc => +loc)
    : [+currentLocationId];

  return { locationIds, isAllLocationSelected };
};

// This fetches only active users for a given company and location
// Only forward isLocationFilter when the caller explicitly passed it (isLocationFilterProvided),
// so the value derived from isCurrentLocationUser doesn't leak in and over-restrict results.
const createActiveUsersPayload = ({ companyId, locationIds = [], search = "", ...rest }) => {
  return {
    companyId: +companyId,
    locationIds,
    search,
    status: [USER_STATUS.ACTIVATED],
    ...(rest?.isAllLocationUsers ? { isAllLocationUsers: true } : {}),
    ...(rest?.isLocationFilterProvided ? { isLocationFilter: rest.isLocationFilter } : {}),
    // ...rest,
  };
};

// This fetches all users (active and deactive) for a given company and location
const createAllUsersPayload = ({
  companyId,
  locationIds = [],
  search = "",
  // Strip the helper flag so it never leaks into the GraphQL variables
  isLocationFilterProvided,
  ...rest
}) => ({
  companyId: +companyId,
  locationIds,
  search,
  allUser: true,
  isAllLocationUsers: true,
  ...rest,
});

// This fetches only deactive users for a given company and location
const createDeactiveUsersPayload = ({ companyId, locationIds = [], search = "", ...rest }) => ({
  companyId: +companyId,
  locationIds,
  search,
  status: [USER_STATUS.DEACTIVATED],
  ...(rest?.isLocationFilterProvided ? { isLocationFilter: rest.isLocationFilter } : {}),
  // ...rest,
});

/**
 * Custom hook to fetch users based on company and other filters.
 * @param {Object} variables - Variables for the query (e.g., companyId, isAllLocationUsers, etc.).
 * @param {Function} onCompleted - Callback function to handle query completion.
 * @param {boolean} skip - Whether to skip the query execution.
 * @param {Object} options - Additional options for preload payloads.
 * @param {boolean} options.useActiveUsersPayload - Use predefined payload for active users only.
 * @param {boolean} options.useAllUsersPayload - Use predefined payload for all users (active + deactive).
 * @param {boolean} options.useDeactiveUsersPayload - Use predefined payload for deactive users only.
 * @param {boolean} options.isCurrentLocationUser - Auto-fetch locationIds from sessionStorage with isLocationFilter.
 * @param {string} options.companyId - Company ID override for preload payloads.
 * @param {Array<number>} options.locationIds - Location IDs override for preload payloads.
 * @param {Object} options.query - GraphQL query to use (default: GET_FILTER_USERS). Use USER_QUERIES.GENERAL for more fields.
 * @param {string} options.fetchPolicy - Apollo fetchPolicy (e.g., 'cache-first', 'network-only', 'cache-and-network').
 * @returns {Object} - Contains data, loading, error, refetch, and userList.
 */
const useFetchUsers = (variables, onCompleted, skip = false, options = {}) => {
  const {
    useActiveUsersPayload = false,
    useAllUsersPayload = false,
    useDeactiveUsersPayload = false,
    isCurrentLocationUser = false,
    companyId: optionCompanyId,
    locationIds: optionLocationIds,
    search = "",
    query = GET_FILTER_USERS,
    fetchPolicy,
    isLocationFilter: isLocationFilterOption,
    ...restOptions
  } = options;

  // Fallbacks: Redux for companyId, sessionStorage for locationIds
  const { user } = useContext(UserContext);

  const reduxCompanyId = useSelector(getCompanyId);
  const companyId = optionCompanyId || reduxCompanyId;

  // If isCurrentLocationUser is true, auto-fetch locationIds and isLocationFilter from sessionStorage
  const { locationIds: sessionLocationIds, isAllLocationSelected } = getSessionLocationIds();
  const locationIds = !isEmpty(optionLocationIds)
    ? optionLocationIds
    : isCurrentLocationUser
    ? sessionLocationIds
    : [];
  // Resolved value: derived from session when using current-location users, else the passed option
  const resolvedIsLocationFilter = isCurrentLocationUser
    ? !isAllLocationSelected
    : isLocationFilterOption;

  const currentLocation = user?.locations?.find(
    location => +location?.location?.id === +sessionStorage.getItem("currentLocationId")
  );
  const isActiveUserRendering =
    currentLocation?.location?.settings?.GENERAL_CONFIG?.SHOW_ACTIVATE_USER_ONLY;

  // Determine which payload to use
  // Priority: isActiveUserRendering (setting) > payload flags > variables prop
  // Note: isCurrentLocationUser is always used for locationIds (separate concern)
  const queryVariables = useMemo(() => {
    const payloadArgs = {
      companyId,
      locationIds,
      search,
      isLocationFilter: resolvedIsLocationFilter,
      // Whether the caller explicitly passed isLocationFilter (vs. it being derived from
      // isCurrentLocationUser). Active/deactive payloads forward the flag only when true.
      isLocationFilterProvided: isLocationFilterOption !== undefined,
      ...restOptions,
    };

    // 1. isActiveUserRendering setting takes highest priority (only when explicitly set)
    // true = active users only, false = all users, undefined = skip to next priority
    if (isActiveUserRendering === true && companyId && !useAllUsersPayload) {
      return createActiveUsersPayload(payloadArgs);
    }
    if (
      !isActiveUserRendering &&
      companyId &&
      !useActiveUsersPayload &&
      !useDeactiveUsersPayload &&
      !useAllUsersPayload
    ) {
      return createAllUsersPayload(payloadArgs);
    }

    // 2. Explicit payload flags (used when isActiveUserRendering is undefined)
    if (useActiveUsersPayload && companyId) {
      return createActiveUsersPayload(payloadArgs);
    }
    if (useAllUsersPayload && companyId) {
      return createAllUsersPayload(payloadArgs);
    }
    if (useDeactiveUsersPayload && companyId) {
      return createDeactiveUsersPayload(payloadArgs);
    }

    return variables;
  }, [
    isActiveUserRendering,
    useActiveUsersPayload,
    useAllUsersPayload,
    useDeactiveUsersPayload,
    companyId,
    locationIds,
    resolvedIsLocationFilter,
    search,
    restOptions,
    variables,
  ]);

  const [userList, setUserList] = useState([]);

  const { data, loading, error, refetch } = useQuery(query, {
    variables: queryVariables,
    skip, // Skip the query if required variables are not ready
    ...(fetchPolicy && { fetchPolicy }), // Only add fetchPolicy if provided
    onCompleted: res => {
      setUserList(res?.getUsers?.data || []);
      onCompleted?.(res);
    },
  });

  useEffect(() => {
    if (data?.getUsers?.data) {
      setUserList(data?.getUsers?.data);
    }
  }, [data]);

  const users = data?.getUsers?.data || [];

  const sortedUsers = useMemo(
    () =>
      sortBy(users, u =>
        (u?.fullName || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "").toLowerCase()
      ),
    [users]
  );

  return { userList, loading, error, refetch, users, sortedUsers };
};

// Helper to get user display name - handles both fullName and firstName/lastName
const getUserDisplayName = user => {
  if (!user) return "";
  if (user.fullName) return user.fullName;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  }
  return "";
};

/**
 * Custom hook that fetches users and appends existing users if they're missing.
 * Useful for scenarios where assigned users might be deactivated but still need to show in dropdown.
 *
 * @param {Object} options - Hook options
 * @param {Array} options.existingUsers - Array of existing user objects to append if missing (e.g., assigned users)
 * @param {boolean} options.skip - Whether to skip the query
 * @param {Object} options - Other options passed to useFetchUsers (query, fetchPolicy, isCurrentLocationUser, etc.)
 * @returns {Object} - { users, loading, error, refetch }
 */
export const useFetchUsersWithExisting = ({
  existingUsers = [],
  skip = false,
  ...fetchOptions
} = {}) => {
  // Filter out invalid existing users (must have id and displayable name)
  const validExistingUsers = useMemo(() => {
    return (existingUsers || []).filter(u => u && u.id && getUserDisplayName(u));
  }, [existingUsers]);

  // Fetch users using the main hook
  const { userList, loading, error, refetch } = useFetchUsers(null, null, skip, fetchOptions);

  // Merge fetched users with existing users using useMemo (no state, no callbacks)
  const mergedUsers = useMemo(() => {
    // If still loading and no data yet, show existing users
    if (loading && userList.length === 0 && validExistingUsers.length > 0) {
      return sortBy(validExistingUsers, u => getUserDisplayName(u));
    }

    // Merge: use uniqBy with String(id) to handle type mismatch
    // Existing users first to preserve them if duplicated
    const merged = uniqBy([...validExistingUsers, ...userList], u => String(u.id));

    return sortBy(merged, u => getUserDisplayName(u));
  }, [userList, validExistingUsers, loading]);

  return {
    users: mergedUsers,
    loading,
    error,
    refetch,
  };
};

export default useFetchUsers;
